import { useEffect, useMemo, useRef, useState } from 'react'
import { useParams } from 'react-router-dom'
import { Trophy, MapPin, Calendar, ChevronRight, ChevronLeft, Maximize2, X } from 'lucide-react'
import { NotFoundError } from '@meiuca/service'
import { MarketplaceHeader } from '@/components/MarketplaceHeader'
import { TournamentStageView, MarkdownContent } from '@meiuca/ui-components'
import { tournamentPublicService, type PublicEventPayload, type PublicEventCategory } from '../services/tournamentPublicService'

const POLL_MS = 20_000

/** Formats an ISO date as "10 jul 2026", reading the stored UTC day so the
 *  date never shifts with the viewer's timezone. */
function formatEventDay(iso: string): string {
  return new Date(iso).toLocaleDateString('pt-BR', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    timeZone: 'UTC'
  })
}

/** A human date range for the event header: single day, a span, or open-ended. */
function eventDateRange(startsOn: string | null, endsOn: string | null): string | null {
  if (startsOn && endsOn) {
    if (startsOn.slice(0, 10) === endsOn.slice(0, 10)) return formatEventDay(startsOn)
    return `${formatEventDay(startsOn)} – ${formatEventDay(endsOn)}`
  }
  if (startsOn) return `A partir de ${formatEventDay(startsOn)}`
  if (endsOn) return `Até ${formatEventDay(endsOn)}`
  return null
}

/**
 * Public follow-along page for a tournament event, reached by its link
 * (`/tournament-events/:token`). A normal page of the site — it wears the site
 * header like any other. No login: the token is the credential.
 *
 * Navigation is overview → detail (not tabs), so it scales to a championship
 * with many categories: the event opens on a list of category cards (status +
 * champion), and tapping one drills into its bracket/table. A single-category
 * event opens straight into the bracket. It refreshes itself every ~20s so the
 * draw advances on its own.
 */
export default function PublicTournamentEventPage() {
  const { token } = useParams<{ token: string }>()
  const [payload, setPayload] = useState<PublicEventPayload | null>(null)
  const [loading, setLoading] = useState(true)
  const [notFound, setNotFound] = useState(false)
  const [error, setError] = useState(false)
  // null = the category overview list; an id = that category's detail.
  const [selectedId, setSelectedId] = useState<string | null>(null)
  const [showFullBanner, setShowFullBanner] = useState(false)

  // Keep the selection stable across polls.
  const selectedIdRef = useRef<string | null>(null)
  selectedIdRef.current = selectedId

  // Close the full-banner viewer with Escape.
  useEffect(() => {
    if (!showFullBanner) return
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setShowFullBanner(false)
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [showFullBanner])

  useEffect(() => {
    if (!token) return
    let active = true

    const load = async (initial: boolean) => {
      try {
        if (initial) setLoading(true)
        const data = await tournamentPublicService.getEvent(token)
        if (!active) return
        setPayload(data)
        setError(false)
        setNotFound(false)
        const cats = data.categories
        if (cats.length === 1) {
          // Single category: skip the list, open it directly.
          setSelectedId(cats[0].id)
        } else if (selectedIdRef.current && !cats.some((c) => c.id === selectedIdRef.current)) {
          // The open category vanished — fall back to the list.
          setSelectedId(null)
        }
      } catch (err) {
        if (!active) return
        if (err instanceof NotFoundError) {
          setNotFound(true)
        } else if (initial) {
          setError(true)
        }
        // On a poll failure (non-initial), keep showing the last good state.
      } finally {
        if (active && initial) setLoading(false)
      }
    }

    void load(true)
    const id = window.setInterval(() => void load(false), POLL_MS)
    return () => {
      active = false
      window.clearInterval(id)
    }
  }, [token])

  const categories = payload?.categories ?? []
  const multiple = categories.length > 1
  const selected = useMemo(
    () => categories.find((c) => c.id === selectedId) ?? null,
    [categories, selectedId]
  )

  return (
    <div className="min-h-screen bg-background-primary">
      <MarketplaceHeader />

      {loading ? (
        <div className="container mx-auto px-4 py-16 flex justify-center">
          <div className="w-8 h-8 border-2 border-border-light border-t-brand-primary rounded-full animate-spin" />
        </div>
      ) : notFound ? (
        <div className="container mx-auto px-4 py-16 text-center">
          <div className="w-16 h-16 bg-background-secondary rounded-full flex items-center justify-center mx-auto mb-4">
            <Trophy className="w-8 h-8 text-text-tertiary" />
          </div>
          <h1 className="text-lg font-medium text-text-primary mb-1">Evento não encontrado</h1>
          <p className="text-text-secondary">O link pode estar incorreto ou o evento não está público.</p>
        </div>
      ) : error || !payload ? (
        <div className="container mx-auto px-4 py-16 text-center text-text-secondary">
          Não foi possível carregar o evento. Tente novamente.
        </div>
      ) : (
        <div className="container mx-auto px-4 py-6 max-w-6xl">
          {/* Event header */}
          <div className="bg-white rounded-xl border border-border-light overflow-hidden mb-4">
            {payload.bannerImageUrl && (
              <button
                type="button"
                onClick={() => setShowFullBanner(true)}
                className="group relative block w-full"
                title="Ver banner completo"
              >
                <img
                  src={payload.bannerImageUrl}
                  alt={payload.name}
                  className="w-full h-40 sm:h-56 object-cover"
                />
                <span className="absolute bottom-2 right-2 inline-flex items-center gap-1 rounded-md bg-black/50 px-2 py-1 text-xs font-medium text-white opacity-90 backdrop-blur-sm transition-opacity group-hover:opacity-100">
                  <Maximize2 className="w-3.5 h-3.5" /> Ver completo
                </span>
              </button>
            )}
            <div className="p-4">
              <h1 className="text-xl sm:text-2xl font-semibold text-text-primary">{payload.name}</h1>
              <div className="mt-1 flex flex-wrap items-center gap-x-4 gap-y-1 text-sm text-text-secondary">
                <span>{payload.ownerName}</span>
                {eventDateRange(payload.startsOn, payload.endsOn) && (
                  <span className="flex items-center gap-1">
                    <Calendar className="w-3.5 h-3.5" /> {eventDateRange(payload.startsOn, payload.endsOn)}
                  </span>
                )}
                {payload.location && (
                  <span className="flex items-center gap-1">
                    <MapPin className="w-3.5 h-3.5" /> {payload.location}
                  </span>
                )}
                {multiple && (
                  <span>
                    {categories.length} categorias
                  </span>
                )}
              </div>
              {payload.description && (
                <div className="mt-3 text-text-secondary">
                  <MarkdownContent className="max-h-48 overflow-y-auto pr-1 sm:max-h-72">
                    {payload.description}
                  </MarkdownContent>
                </div>
              )}
            </div>
          </div>

          {categories.length === 0 ? (
            <p className="text-text-secondary text-center py-12">Nenhuma categoria ainda.</p>
          ) : selected ? (
            <CategoryDetail
              category={selected}
              categories={categories}
              showBack={multiple}
              onBack={() => setSelectedId(null)}
              onSwitch={setSelectedId}
            />
          ) : (
            <CategoryList categories={categories} onOpen={setSelectedId} />
          )}
        </div>
      )}

      {showFullBanner && payload?.bannerImageUrl && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 p-4"
          onClick={() => setShowFullBanner(false)}
          role="dialog"
          aria-modal="true"
        >
          <img
            src={payload.bannerImageUrl}
            alt={payload.name}
            className="max-h-[90vh] max-w-[95vw] rounded-lg object-contain shadow-2xl"
          />
          <button
            type="button"
            onClick={() => setShowFullBanner(false)}
            className="absolute right-4 top-4 rounded-full bg-white/10 p-2 text-white transition-colors hover:bg-white/20"
            aria-label="Fechar"
          >
            <X className="h-5 w-5" />
          </button>
        </div>
      )}
    </div>
  )
}

interface StatusStyle {
  label: string
  cls: string
}

function statusStyle(status: string): StatusStyle {
  switch (status) {
    case 'completed':
      return { label: 'Concluída', cls: 'bg-emerald-50 text-emerald-700' }
    case 'inProgress':
      return { label: 'Em andamento', cls: 'bg-blue-50 text-blue-700' }
    default:
      return { label: 'Não começou', cls: 'bg-background-secondary text-text-secondary' }
  }
}

function StatusChip({ status }: { status: string }) {
  const s = statusStyle(status)
  return <span className={`text-xs font-medium px-2 py-0.5 rounded-full whitespace-nowrap ${s.cls}`}>{s.label}</span>
}

/** The event's overview: a card per category, tappable into its bracket. */
function CategoryList({
  categories,
  onOpen
}: {
  categories: PublicEventCategory[]
  onOpen: (id: string) => void
}) {
  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
      {categories.map((category) => (
        <button
          key={category.id}
          onClick={() => onOpen(category.id)}
          className="bg-white rounded-xl border border-border-light p-4 text-left flex items-center gap-3 transition-colors hover:border-brand-primary/60"
        >
          <div className="flex-1 min-w-0">
            <div className="font-medium text-text-primary truncate">{category.name}</div>
            {category.status === 'completed' && category.championName && (
              <div className="mt-1 flex items-center gap-1.5 text-sm text-text-secondary truncate">
                <Trophy className="w-3.5 h-3.5 text-amber-500 flex-shrink-0" />
                <span className="truncate">Campeão: {category.championName}</span>
              </div>
            )}
          </div>
          <StatusChip status={category.status} />
          <ChevronRight className="w-5 h-5 text-text-tertiary flex-shrink-0" />
        </button>
      ))}
    </div>
  )
}

/** One category's bracket/table, with a way back to the list and a quick switch. */
function CategoryDetail({
  category,
  categories,
  showBack,
  onBack,
  onSwitch
}: {
  category: PublicEventCategory
  categories: PublicEventCategory[]
  showBack: boolean
  onBack: () => void
  onSwitch: (id: string) => void
}) {
  return (
    <div className="space-y-3">
      <div className="flex items-center gap-3">
        {showBack && (
          <button
            onClick={onBack}
            className="flex items-center gap-1 text-sm font-medium text-text-secondary hover:text-text-primary whitespace-nowrap"
          >
            <ChevronLeft className="w-4 h-4" /> Categorias
          </button>
        )}
        <h2 className="text-lg font-semibold text-text-primary flex-1 truncate">{category.name}</h2>
        <StatusChip status={category.status} />
        {showBack && (
          <select
            value={category.id}
            onChange={(event) => onSwitch(event.target.value)}
            aria-label="Trocar de categoria"
            className="text-sm border border-border-light rounded-lg px-2 py-1.5 bg-white text-text-primary max-w-[10rem]"
          >
            {categories.map((c) => (
              <option key={c.id} value={c.id}>
                {c.name}
              </option>
            ))}
          </select>
        )}
      </div>

      {category.status === 'completed' && category.championName && (
        <div className="bg-amber-50 border border-amber-200 rounded-lg px-4 py-2.5 flex items-center gap-2">
          <Trophy className="w-5 h-5 text-amber-500 flex-shrink-0" />
          <span className="text-sm font-semibold text-amber-800">Campeão: {category.championName}</span>
        </div>
      )}

      {!category.stage ? (
        <p className="text-text-secondary py-8">Chave em breve.</p>
      ) : (
        <div className="bg-white rounded-xl border border-border-light p-4 h-[70vh]">
          <TournamentStageView stage={category.stage} />
        </div>
      )}
    </div>
  )
}
